import React, { useMemo } from 'react'
import ReactApexChart from 'react-apexcharts'

const EnergyPhasesComponent = ({ sensors, height = 500 }) => {
  const phaseKeys = useMemo(() => (sensors ? Object.keys(sensors) : []), [sensors])

  const allDays = useMemo(() => {
    if (!sensors || phaseKeys.length === 0) return []
    const daysSet = new Set()
    phaseKeys.forEach(phase => {
      if (sensors[phase].days) {
        Object.keys(sensors[phase].days).forEach(day => daysSet.add(day))
      }
    })
    return Array.from(daysSet).sort((a, b) => Number(a) - Number(b))
  }, [sensors, phaseKeys])

  const series = useMemo(() => {
    if (allDays.length === 0) return []
    
    const kwhSeries = phaseKeys.map(phase => ({
      name: `${phase} (kWh)`,
      type: 'bar',
      group: 'consumo',
      data: allDays.map(day => sensors[phase].days[day]?.kwh || 0),
      prices: allDays.map(day => sensors[phase].days[day]?.value || 0)
    }))

    const valueSeries = phaseKeys.map(phase => ({
      name: `${phase} (R$)`,
      type: 'bar',
      group: 'valor',
      data: allDays.map(day => sensors[phase].days[day]?.value || 0),
      consumptions: allDays.map(day => sensors[phase].days[day]?.kwh || 0)
    }))

    const accumulatedSeries = phaseKeys.map(phase => ({
      name: `Acumulado ${phase} (kWh)`,
      type: 'line',
      group: 'consumo',
      data: allDays.map(day => sensors[phase].days[day]?.accumulated_value || 0)
    }))

    const totalKwhData = allDays.map(day => 
      phaseKeys.reduce((acc, phase) => acc + (sensors[phase].days[day]?.accumulated_value || 0), 0)
    )

    const totalLine = {
      name: 'Total Geral (kWh)',
      type: 'line',
      group: 'consumo',
      data: totalKwhData
    }

    return [...kwhSeries, ...valueSeries, ...accumulatedSeries, totalLine]
  }, [sensors, phaseKeys, allDays])

  const options = {
    chart: {
      type: 'bar',
      height: height,
      stacked: true,
      toolbar: { show: false },
      clip: false 
    },
    stroke: {
      width: series.map(s => s.type === 'line' ? 3 : 1),
      curve: 'smooth'
    },
    colors: [
      '#80c7fd', '#008FFB', '#1e40af', 
      '#80f1cb', '#00E396', '#059669', 
      '#FF4560', '#FEB019', '#775DD0', '#334155'
    ],
    dataLabels: { enabled: false },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '85%',
        dataLabels: {
          total: {
            enabled: true,
            offsetY: 105, 
            style: { fontSize: '11px', fontWeight: 900, color: '#334155' },
            formatter: (val, { seriesIndex, w }) => {
              const s = w.config.series[seriesIndex]
              if (!s) return ''
              return s.group === 'valor' ? `R$ ${val.toFixed(2)}` : `${val.toFixed(1)} kWh`
            }
          }
        }
      }
    },
    markers: {
      size: series.map(s => s.type === 'line' ? 4 : 0)
    },
    xaxis: {
      categories: allDays,
      title: { text: 'Dias do Mês', style: { fontWeight: 600 } }
    },
    yaxis: {
      max: (max) => max * 1.1,
      tickAmount: 5,
      labels: {
        formatter: (val) => val.toFixed(0)
      }
    },
    fill: { opacity: 1 },
    grid: {
      padding: { top: 30 },
      borderColor: '#f1f5f9'
    },
    legend: {
      position: 'top',
      horizontalAlign: 'center',
      offsetY: 0
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val, { seriesIndex, dataPointIndex, w }) => {
          const s = w.config.series[seriesIndex];
          if (s.type === 'line') return `${val.toFixed(2)} kWh`;
          if (s.group === 'consumo') {
            const price = s.prices ? s.prices[dataPointIndex] : 0;
            return `${val.toFixed(2)} kWh (R$ ${price.toFixed(2)})`;
          } else {
            const kwh = s.consumptions ? s.consumptions[dataPointIndex] : 0;
            return `R$ ${val.toFixed(2)} (${kwh.toFixed(2)} kWh)`;
          }
        }
      }
    }
  }

  if (series.length === 0) return null

  return (
    <div style={{ 
      border: '1px solid #e2e8f0', 
      borderRadius: '16px', 
      background: '#fff', 
      marginTop: '20px', 
      padding: '10px', 
      minHeight: height + 50 
    }}>
      <ReactApexChart 
        options={options} 
        series={series} 
        type="bar" 
        height={height} 
      />
    </div>
  )
}

export default EnergyPhasesComponent